/*
* Created on Wed Oct 11 2023 4:46:23 PM
*
* ***************************************************************************************
* Copyright (c) AVPS Technologies Inc 2023 
*
* All Rights Reserved
* Unpublished rights are reserved under the copyright laws of the United States.
* The software contained in this media is proprietary to and embodies confidential
* the technology of AVPS Technologies Inc. Possession, use, duplication or
* dissemination of the software and media is authorized only pursuant to a valid
* written license from AVPS Technologies Inc.
*
* The above copyright notice and this permission notice shall be included in
* all copies or substantial portions of the Software.
*
* RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
* is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
* 252.227-7013 or in FAR 52.227-19, as applicable.
****************************************************************************************
*/
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Building2, Coffee, Headphones, Sparkles, X } from 'lucide-react';
import moment from 'moment-timezone';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getReservationSlots } from '../../../../../api/action/booking';
import { findEligibleDiscounts } from '../../../../../api/action/discount';
import { getAllProductCategories } from '../../../../../api/action/products';
import { fetchStudentById } from '../../../../../api/action/user';
import DiscountDisplay from '../components/DiscountDisplay';
import WorkspaceCard from '../components/WorkspaceCard';
import BookingForm from './BookingForm';
import SearchResults from './SearchResults';
import EnsurePersonalPreference from '../../../../../components/useEnsurePersonalPreference';

const BookingMain = () => {
  const companyPrimaryId = useSelector((state) => state.companyReducer.company?.primaryId) || '';
  const companyState = useSelector((state) => state?.companyReducer?.company) || {};
  const companyTimezone = companyState?.operatingLocation?.zoneId || 'America/New_York';

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const presetCategoryId = location.state?.presetCategoryId;
  const appliedPresetCategoryRef = useRef(false);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const [searchCriteria, setSearchCriteria] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [hasDiscounts, setHasDiscounts] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isModifySearch, setIsModifySearch] = useState(false);

  const heroRef = useRef(null);
  const topRef = useRef(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const [searchParams] = useSearchParams();
  const guestPrimaryIdParam = searchParams.get('guestPrimaryId');
  const guestAccountIdParam = searchParams.get('guestAccountId');
  const encryptedEmailParam = searchParams.get('email');
  const encryptedPasswordParam = searchParams.get('password');



  useEffect(() => {
    let isMounted = true;
    const loadCustomerData = async () => {
      try {
        setIsLoading(true);
        const guestPrimaryId = guestPrimaryIdParam;
        if (!guestPrimaryId) {
          if (isMounted) setIsLoading(false);
          return;
        }

        const guestAccountId = guestAccountIdParam;
        if (guestAccountId) {
          sessionStorage.setItem("guestAccountId", guestAccountId);
        }
        const encryptedEmail = encryptedEmailParam;
        if (encryptedEmail) {
          sessionStorage.setItem("encryptedEmail", encryptedEmail);
        }

        const encryptedPassword = encryptedPasswordParam;
        if (encryptedPassword) {
          sessionStorage.setItem("encryptedPassword", encryptedPassword);
        }

        const response = await dispatch(fetchStudentById({ primaryId: guestPrimaryId }));

        let studentDataArray;
        if (response.payload) {
          studentDataArray = response.payload;
        } else if (Array.isArray(response)) {
          studentDataArray = response;
        } else if (response.data) {
          studentDataArray = response.data;
        }

        if (studentDataArray && Array.isArray(studentDataArray) && studentDataArray.length > 0) {
          const studentData = studentDataArray[0];
          localStorage.setItem("guestAuthenticated", "true");
          sessionStorage.setItem("guestAccountId", studentData?.account?.primaryId || '');
          sessionStorage.setItem("guestshoppingCartId", studentData?.account?.shoppingCart?.primaryId || '');
          sessionStorage.setItem("guestData", JSON.stringify(studentData) || '');
        }
      } catch {
        sessionStorage.removeItem("guestAccountId");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadCustomerData();
    return () => {
      isMounted = false;
    };
  }, [dispatch, encryptedEmailParam, encryptedPasswordParam, guestAccountIdParam, guestPrimaryIdParam]);


  const fetchProducts = useCallback(async (company) => {
    try {
      setIsLoading(true);
      const res = await dispatch(getAllProductCategories(company));
      if (res && Array.isArray(res)) {
        setProducts(res.filter(p => p?.type?.toUpperCase() !== 'ADD_ONS'));
      }
    } catch {
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const extractProductIdsFromGroups = (groups = []) => {
    if (!Array.isArray(groups) || groups.length === 0) return [];

    const productIds = new Set();

    groups.forEach((group) => {
      if (group.productIds && Array.isArray(group.productIds)) {
        group.productIds.forEach(id => productIds.add(id));
      }
    });

    return Array.from(productIds);
  };

  const fetchDiscounts = async (matchingProductGroups = []) => {
    if (!companyPrimaryId || !matchingProductGroups.length) return [];

    try {
      const productIds = extractProductIdsFromGroups(matchingProductGroups);

      if (productIds.length === 0) return [];

      const payload = {
        associatedProducts: productIds.map(id => ({
          primaryId: id,
          company: {
            primaryId: companyPrimaryId
          }
        })),
        criteria: {
          productAttributes: productIds.map(id => ({
            primaryId: id
          })),
          durationTypes: ["HOUR"]
        },
        status: "ACTIVE"
      };

      const result = await dispatch(findEligibleDiscounts(payload));

      let extractedDiscounts = [];

      if (Array.isArray(result)) {
        extractedDiscounts = result;
      } else if (result?.payload && Array.isArray(result.payload)) {
        extractedDiscounts = result.payload;
      } else if (result?.data && Array.isArray(result.data)) {
        extractedDiscounts = result.data;
      } else if (result?.eligibleDiscounts && Array.isArray(result.eligibleDiscounts)) {
        extractedDiscounts = result.eligibleDiscounts;
      } else {
        extractedDiscounts = result ? [result] : [];
      }

      // Filter out expired and not-yet-started discounts
      const now = new Date();
      const validDiscounts = extractedDiscounts.filter(discount => {
        // Check if discount is active
        if (discount.status !== 'ACTIVE') return false;

        // Check date validity if timePeriod exists
        if (discount.timePeriod?.effectiveDate) {
          const startDate = new Date(discount.timePeriod.effectiveDate.effectiveStartDate);
          const endDate = new Date(discount.timePeriod.effectiveDate.effectiveEndDate);

          // Ignore if not started yet or already ended
          if (now < startDate || now > endDate) {
            return false;
          }
        }

        return true;
      });

      setHasDiscounts(validDiscounts.length > 0);
      return validDiscounts;
    } catch {
      setHasDiscounts(false);
      return [];
    }
  };

  const handleBookSpace = (categoryPrimaryId = null) => {
    const selectedCat = products.find(p => p.primaryId === categoryPrimaryId);
    setSelectedCategoryId(categoryPrimaryId);
    setSelectedCategory(selectedCat);
    setCurrentView('booking');
    setIsModifySearch(false);
    setTimeout(() => { scrollToTop(); }, 100);
  };

  const handleSearch = async (criteria) => {
    try {
      setIsSearching(true);
      setSearchCriteria(criteria);

      const categoryId = criteria.categoryId || selectedCategoryId;

      if (!categoryId) {
        setIsSearching(false);
        return;
      }

      if (!selectedCategory && categoryId) {
        const category = products.find(p => p.primaryId === categoryId);
        setSelectedCategory(category);
      }

      const selectedDateMoment = moment.tz(criteria.date, 'YYYY-MM-DD', companyTimezone);
      const formattedDate = selectedDateMoment.format('YYYY-MM-DDTHH:mm:ss');
      const companyWorkingSchedule = {
        ...companyState?.workingSchedule,
        workingDays: companyState?.workingSchedule?.workingDays || [],
        holidays: companyState?.workingSchedule?.holidays || [],
        openHours: companyState?.workingSchedule?.openHours || '08:00',
        closeHours: companyState?.workingSchedule?.closeHours || '18:00',
        slotIntervalInMinutes: companyState?.workingSchedule?.slotIntervalInMinutes || 60
      };
      const reservationPayload = {
        company: {
          primaryId: companyPrimaryId,
          domain: { type: "FLEX_OFFICE" },
          workingSchedule: companyWorkingSchedule
        },
        criteria: {
          durationType: criteria.duration || "HOUR",
          product: {
            category: { primaryId: categoryId },
            status: "ACTIVE",
            ...(criteria.people && { capacity: criteria.people })
          },
          searchPeriod: {
            effectiveDate: {
              localStartDate: formattedDate,
              timeZone: companyTimezone
            }
          }
        }
      };

      const results = await dispatch(getReservationSlots(reservationPayload));

      const hasResults = results?.matchingProductGroups?.length > 0;

      if (!hasResults) {
        setSearchResults(results || { matchingProductGroups: [] });
        setCurrentView('results');

        const discountData = await fetchDiscounts([]);
        setDiscounts(discountData);

        setTimeout(() => { scrollToTop(); }, 100);
        setIsSearching(false);
        return;
      }

      setSearchResults(results);
      setCurrentView('results');

      const discountData = await fetchDiscounts(results?.matchingProductGroups || []);
      setDiscounts(discountData);

      setTimeout(() => { scrollToTop(); }, 100);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleProductSelect = async (productData) => {
    const { selectedTime: timeSlot, totalPrice, ...product } = productData;

    const productDiscounts = await fetchDiscounts(searchResults?.matchingProductGroups || []);

    // Navigate immediately once the data we need is ready. Updating local
    // state (category, discounts) before this point re-renders SearchResults
    // while still on this screen, which rebuilds the product list and wipes
    // the user's slot selection right before the redirect — the "blink".
    navigate('/selected-product-details', {
      state: {
        product,
        searchCriteria,
        selectedTime: timeSlot,
        discounts: productDiscounts
      }
    });

    if (product.category && product.category.primaryId) {
      setSelectedCategoryId(product.category.primaryId);
      setSelectedCategory(product?.category);
    }
    setDiscounts(productDiscounts);
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedCategoryId(null);
    setSelectedCategory(null);
    setSearchResults(null);
    setShowDiscountPanel(false);
    setHasDiscounts(false);
    setIsModifySearch(false);
    setTimeout(() => { scrollToTop(); }, 100);
  };

  const handleBackToSearch = () => {
    setCurrentView('booking');
    setSearchResults(null);
    setTimeout(() => { scrollToTop(); }, 100);
  };

  const handleModifySearch = () => {
    setCurrentView('booking');
    setIsModifySearch(true);
    setTimeout(() => { scrollToTop(); }, 100);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    const selectedCat = products.find(p => p.primaryId === categoryId);
    setSelectedCategory(selectedCat);
  };

  useLayoutEffect(() => {
    fetchProducts(companyPrimaryId);
  }, [companyPrimaryId, fetchProducts]);

  // Lets a caller (e.g. the Kiosk landing page's category cards) deep-link
  // straight into "pick a date/time for this category" without changing any
  // existing behavior for a normal visitor, who never sets this state.
  useEffect(() => {
    if (!presetCategoryId || appliedPresetCategoryRef.current || products.length === 0) return;
    appliedPresetCategoryRef.current = true;
    handleBookSpace(presetCategoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetCategoryId, products]);

  const generateWorkspaceData = (product, index) => {
    const icons = [<Building2 className="w-8 h-8" />, <Coffee className="w-8 h-8" />, <Headphones className="w-8 h-8" />];
    const ratings = ["4.9", "4.8", "4.7", "4.6", "4.9", "4.8"];

    return {
      title: product.name,
      description: product.description || `Premium ${product.name.toLowerCase()} designed for productivity and comfort`,
      icon: icons[index % icons.length],
      primaryId: product.primaryId,
      imageURL: product?.imageContent?.displayLink,
      rating: ratings[index % ratings.length]
    };
  };

  const workspaceCategories = products.slice(0, 6).map((product, index) =>
    generateWorkspaceData(product, index)
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50/50 relative overflow-hidden">
      {/* Ambient background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-100/40 rounded-full blur-3xl" />
      </div>

      <div ref={topRef} className="absolute top-0" />
      {/* Personal Preference update after login  */}
      <EnsurePersonalPreference />


      {/* Discount Panel */}
      <AnimatePresence>
        {currentView === 'results' && hasDiscounts && (
          <>
            {showDiscountPanel && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40"
                  onClick={() => setShowDiscountPanel(false)}
                />
                <motion.div
                  initial={{ x: '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '100%', opacity: 0 }}
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                  className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
                  style={{ borderTopLeftRadius: '24px', borderBottomLeftRadius: '24px' }}
                >
                  {/* Panel Header */}
                  <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 px-8 py-8">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
                    <div className="relative flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', delay: 0.2 }}
                          className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl"
                        >
                          <Sparkles className="w-7 h-7 text-white" />
                        </motion.div>
                        <div>
                          <motion.h3
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-2xl font-bold text-white mb-1"
                          >
                            Special Offers
                          </motion.h3>
                          <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="text-blue-100 text-sm font-medium"
                          >
                            {discounts.length} exclusive discount{discounts.length > 1 ? 's' : ''} available
                          </motion.p>
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setShowDiscountPanel(false)}
                        className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm"
                      >
                        <X className="w-5 h-5 text-white" />
                      </motion.button>
                    </div>
                  </div>

                  {/* Panel Content */}
                  <div className="flex-1 overflow-y-auto px-6 py-8">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="space-y-4"
                    >
                      <DiscountDisplay discounts={discounts} />
                    </motion.div>
                  </div>

                  {/* Panel Footer */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="border-t border-slate-100 p-6 bg-gradient-to-b from-white to-slate-50/50"
                  >
                    <div className="flex items-center justify-center space-x-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <p className="font-medium">Discounts can be applied at checkout</p>
                    </div>
                  </motion.div>
                </motion.div>
              </>
            )}
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <section ref={heroRef} className="relative py-10 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          {(currentView === 'home' || currentView === 'booking') && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center mb-8 sm:mb-16 lg:mb-24"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="inline-flex items-center space-x-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/50 rounded-full mb-4 sm:mb-8"
              >
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-xs sm:text-sm font-semibold text-blue-700">Premium Workspaces</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-3xl sm:text-5xl lg:text-7xl font-extrabold text-slate-900 mb-3 sm:mb-6 tracking-tight leading-[1.15] sm:leading-[1.1]"
              >
                Spaces built for
                <br />
                <span className="relative inline-block mt-2">
                  <span className="relative z-10 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
                    productivity
                  </span>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                    className="absolute bottom-2 left-0 right-0 h-3 bg-blue-100/50 -z-0 origin-left"
                    style={{ borderRadius: '6px' }}
                  />
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="text-base sm:text-xl lg:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-medium fx-m-hide"
              >
                Every location is carefully curated with cutting-edge amenities and designed
                to inspire your most productive work.
              </motion.p>
            </motion.div>
          )}

          {/* Booking Form Section */}
          {(currentView === 'booking' || currentView === 'results' || currentView === 'details') && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 sm:mb-16"
            >
              <BookingForm
                onSearch={handleSearch}
                onBack={handleBackToHome}
                selectedCategoryId={selectedCategoryId}
                isLoading={isSearching}
                selectedCategory={selectedCategory}
                companyTimezone={companyTimezone}
                categories={products}
                onCategoryChange={handleCategoryChange}
                isModifySearch={isModifySearch}
                searchCriteria={searchCriteria}
                key={`booking-form-${isModifySearch}-${selectedCategoryId}`}
              />
            </motion.div>
          )}

          {/* Search Results Section */}
          {currentView === 'results' && searchResults && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <SearchResults
                results={searchResults}
                searchCriteria={searchCriteria}
                onSelectProduct={handleProductSelect}
                onBackToSearch={handleBackToSearch}
                onModifySearch={handleModifySearch}
                hasDiscounts={hasDiscounts}
                onShowDiscounts={() => setShowDiscountPanel(true)}
                onUpdatePeople={(newPeopleCount) => {
                  setSearchCriteria(prev => ({ ...prev, people: newPeopleCount }));
                }}
                companyTimezone={companyTimezone}
              />
            </motion.div>
          )}

          {/* Home View - Workspace Cards */}
          {currentView === 'home' && (
            <>
              {isLoading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: i * 0.1 }}
                      className="bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
                      style={{ borderRadius: '24px' }}
                    >
                      <div className="relative h-64 bg-gradient-to-br from-slate-100 to-slate-200 animate-pulse">
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-300/50 to-transparent" />
                      </div>
                      <div className="p-7">
                        <div className="h-6 bg-slate-200 rounded-lg w-3/4 mb-4 animate-pulse" />
                        <div className="space-y-2 mb-6">
                          <div className="h-4 bg-slate-200 rounded-lg w-full animate-pulse" />
                          <div className="h-4 bg-slate-200 rounded-lg w-5/6 animate-pulse" />
                        </div>
                        <div className="h-14 bg-slate-200 rounded-2xl animate-pulse" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
                  >
                    {workspaceCategories.map((workspace, index) => (
                      <WorkspaceCard
                        key={workspace.primaryId}
                        {...workspace}
                        index={index}
                        onBook={() => handleBookSpace(workspace.primaryId)}
                      />
                    ))}
                  </motion.div>

                  {/* View All Spaces Button */}
                  {products.length > 6 && (
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      viewport={{ once: true }}
                      className="text-center mt-20"
                    >
                      <motion.button
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/type-of-spaces', { state: { products } })}
                        className="group relative inline-flex items-center px-10 py-5 bg-white border-2 border-slate-200 text-slate-800 font-bold text-lg hover:border-blue-300 hover:text-blue-700 transition-all duration-300 shadow-lg hover:shadow-2xl overflow-hidden"
                        style={{ borderRadius: '20px' }}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: 0 }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10">Explore All Spaces</span>
                        <motion.div
                          className="relative z-10 ml-3"
                          animate={{ x: [0, 4, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        >
                          <ArrowRight className="w-6 h-6" />
                        </motion.div>
                      </motion.button>
                    </motion.div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>




      {/* Decorative bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
    </div>
  );
};

export default BookingMain;
